import { util } from "../util";

/**
 * Handles throttling of requests - based on the following F&O documentation:
 * https://docs.microsoft.com/en-us/dynamics365/fin-ops-core/dev-itpro/data-entities/priority-based-throttling.
 */
export abstract class ThrottlingHandler {

    /** The maximum number of retries that should be attempted by the throttling handler. */
    protected static MaxRetryCount = 5;

    /** The default seconds that the throttling handler should delay a retry attempt if no "Retry-After" header is present on the throttled response. */
    private static DefaultWaitSeconds = 10;

    /** The number of milliseconds in a second. */
    private static MillisecondsPerSecond = 1000;

    /** The current number of request attempts that have been made. */
    private requestAttempts: number;

    /** The activity ID used by the last issued request. */
    protected activityId: string;

    /**
     * @param makeRequest Issues the request that could result in throttling.
     * @param requestThrottled Executed whenever a throttled response is encountered.
     */
    constructor(private makeRequest: (activityId: string) => void, private requestThrottled?: (activityId: string, retryDelay: string, retryAttempt: number, retrying: boolean) => void) {
        this.requestAttempts = 0;
    }

    /** Executes the make request logic provided to the handler. */
    public send(): void {
        this.requestAttempts++;
        this.activityId = util.generateGuid();
        this.makeRequest(this.activityId);
    }

    /**
     * Handles a potentially throttled response, taking in the received status code and retry header. This method must be called whenever a
     * response is received by the appropriate request issuing implementation.
     * @param statusCode The received response's status code.
     * @param retryHeader The received response's "Retry-After" header.
     * @returns A value indicating whether the response was in fact throttled and a subsequent retry was attempted.
     */
    protected handleThrottleResponseInternal(statusCode: number | string, retryHeader: string): boolean {
        let handled = false;
        // Intentionally doing a non-strict compare as the type of the status code isn't consistent (sometimes numeric, sometimes a string)
        if (statusCode == 429) {
            if (this.requestAttempts <= ThrottlingHandler.MaxRetryCount) {
                window.setTimeout(() => this.send(), ThrottlingHandler.determineWaitMilliseconds(retryHeader));
                handled = true;
            }
            this.requestThrottled && this.requestThrottled(this.activityId, retryHeader ?? "default", this.requestAttempts, handled);
        }
        return handled;
    }

    /**
     * Determines the number of milliseconds that the throttling logic should delay before reissuing the request.
     * @param retryHeader The received "Retry-After" header value.
     * @returns The number of milliseconds that the throttling logic should delay before reissuing the request.
     */
    protected static determineWaitMilliseconds(retryHeader: string): number {
        let retryWait = Number(retryHeader);
        retryWait = isNaN(retryWait) ? ThrottlingHandler.DefaultWaitSeconds : (retryWait <= 0 ? 1 : retryWait);
        return retryWait * ThrottlingHandler.MillisecondsPerSecond;
    }
}
